import React, { useState } from "react";
import {
  Box,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

const relativeTimeFormatter = new Intl.RelativeTimeFormat("pt-BR", {
  numeric: "auto",
});

const absoluteDateFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "America/Sao_Paulo",
});

const formatRelativeTime = (dateString) => {
  if (!dateString) return "Agora";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Agora";

  const diffMs = date.getTime() - Date.now();
  const absMinutes = Math.round(Math.abs(diffMs) / 60000);
  const absHours = Math.round(Math.abs(diffMs) / 3600000);
  const absDays = Math.round(Math.abs(diffMs) / 86400000);

  if (absMinutes < 1) return "Agora";
  if (absMinutes < 60) {
    return relativeTimeFormatter.format(Math.round(diffMs / 60000), "minute");
  }
  if (absHours < 24) {
    return relativeTimeFormatter.format(Math.round(diffMs / 3600000), "hour");
  }
  if (absDays < 7) {
    return relativeTimeFormatter.format(Math.round(diffMs / 86400000), "day");
  }

  return absoluteDateFormatter.format(date);
};

function Header({ notifications, onNotificationClick, onMarkAllRead }) {
  const [anchorEl, setAnchorEl] = useState(null);

  // Abre o menu de notificações
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Fecha o menu de notificações
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    onNotificationClick(notification);
    handleClose();
  };

  const unreadCount = notifications.length;

  return (
    <Box
      sx={{
        position: "absolute",
        top: { xs: 8, sm: 10 },
        right: { xs: 8, sm: 10 },
        zIndex: 1200,
      }}
    >
      <IconButton
        color="inherit"
        onClick={handleOpen}
        sx={{
          width: 42,
          height: 42,
          bgcolor: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.14)",
          "&:hover": {
            bgcolor: "rgba(255,255,255,0.14)",
          },
        }}
      >
        <Badge
          badgeContent={unreadCount > 9 ? "9+" : unreadCount}
          color="error"
          invisible={unreadCount === 0}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: { xs: "calc(100vw - 16px)", sm: 380 },
            maxWidth: "100vw",
            maxHeight: { xs: "72vh", sm: 520 },
            borderRadius: 3,
            mt: 1,
            overflow: "hidden",
            bgcolor: "#101A2B",
            border: "1px solid rgba(255,255,255,0.12)",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#FFFFFF" }}>
            Notificações
          </Typography>
          {notifications.length > 0 && (
            <Button
              size="small"
              onClick={() => {
                onMarkAllRead();
                handleClose();
              }}
              startIcon={<DoneAllIcon fontSize="small" />}
              sx={{
                textTransform: "none",
                fontSize: 12,
                color: "#8AB4FF",
                minWidth: "auto",
                px: 1,
              }}
            >
              Marcar lidas
            </Button>
          )}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

        {notifications.length > 0 ? (
          <Box sx={{ maxHeight: { xs: "58vh", sm: 420 }, overflowY: "auto" }}>
            {notifications.map((n, index) => (
              <MenuItem
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                sx={{
                  alignItems: "flex-start",
                  gap: 1.2,
                  py: 1.5,
                  px: 2,
                  whiteSpace: "normal",
                  borderBottom:
                    index !== notifications.length - 1
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "none",
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#1877F2",
                    mt: 1,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#FFFFFF",
                      lineHeight: 1.2,
                      fontSize: 14,
                    }}
                  >
                    {n.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.82)",
                      mt: 0.4,
                      lineHeight: 1.25,
                      fontSize: 13,
                    }}
                  >
                    {n.message}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.56)",
                      mt: 0.6,
                      fontSize: 12,
                    }}
                  >
                    {formatRelativeTime(n.publishedAt)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              px: 2,
              py: 5,
              textAlign: "center",
              color: "rgba(255,255,255,0.72)",
            }}
          >
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 28, mb: 1 }} />
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Tudo em dia</Typography>
            <Typography sx={{ fontSize: 13 }}>
              Nenhuma notificação nova no momento.
            </Typography>
          </Box>
        )}
      </Menu>
    </Box>
  );
}

export default Header;
